import React, { useState } from "react";
import {
  Table,
  Container,
  Button,
  Form,
  Row,
  Col,
  Image,
  Spinner,
} from "react-bootstrap";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Label,
} from "recharts";
import { googleApi } from "../../services/api";
import { FaStar, FaSearch } from "react-icons/fa";

const ReviewAndRatingManagement = () => {
  const [packageName, setPackageName] = useState("");
  const [reviewData, setReviewData] = useState([]);
  const [sentimentData, setSentimentData] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false); // New loading state

  const handleInputChange = (e) => {
    setPackageName(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); // Set loading to true

    try {
      const { data } = await googleApi.get("analyze_reviews/", {
                  params: { package_name: packageName },
                });

      setReviewData(data?.sentiment_analysis ?? []);
      setSuggestions(data?.suggestions ?? []);

      // Prepare sentiment data for charts
      const sentimentCounts = (data?.sentiment_analysis ?? []).reduce(
        (acc, { sentiment }) => {
          acc[sentiment] = (acc[sentiment] || 0) + 1;
          return acc;
        },
        {}
      );
      const chartData = Object.entries(sentimentCounts).map(([sentiment, count]) => ({
        sentiment,
        count,
      }));
      setSentimentData(chartData);
    } catch (error) {
      console.error("Error getting reviews analysis:", error);
      if (error?.response?.status === 404) {
        setReviewData([]);
        setSuggestions([]);
        setSentimentData([]);
      }
    } finally {
      setLoading(false); // Set loading to false
    }
  };

  const renderStars = (score) => {
    const totalStars = 5;
    let stars = [];

    for (let i = 1; i <= totalStars; i++) {
      stars.push(
        <span key={i} className={`star ${i <= score ? "filled" : "empty"}`}>
          ★
        </span>
      );
    }
    return stars;
  };

  return (
    <Container className="mt-4">
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <FaStar className="mr-3 text-yellow-500" />
          Reviews & Ratings Analysis
        </h2>
        <Form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                <div className="md:col-span-10">
                    <Form.Label className="block text-sm font-medium text-gray-700 mb-1">Enter Package Name</Form.Label>
                    <Form.Control
                        type="text"
                        value={packageName}
                        onChange={handleInputChange}
                        placeholder="e.g., com.example.app"
                        className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm text-gray-700 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition duration-200"
                        style={{ fontSize: "0.9rem", padding: "8px 12px", height: "42px" }}
                        required
                    />
                </div>
                <div className="md:col-span-2">
                    <Button
                        variant="primary"
                        type="submit"
                        className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded-md shadow transition duration-200 flex items-center justify-center border-none"
                        style={{ fontSize: "0.9rem", padding: "8px 12px", height: "42px" }}
                    >
                        {loading ? <Spinner animation="border" size="sm" /> : <><FaSearch className="mr-2"/> Analyze</>}
                    </Button>
                </div>
            </div>
        </Form>
      </div>

      {loading && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "300px" }}
        >
          <Spinner animation="border" role="status" />
          <span className="ms-2">Loading Please Wait...</span>
        </div>
      )}

      {!loading && (
        <>
          <Row>
            <Col md={7} className="mb-3">
              <div
                className="card p-3"
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  height: "250px",
                  fontSize: "0.6rem",
                }}
              >
                <LineChart
                  width={450}
                  height={250}
                  data={sentimentData}
                  margin={{ top: 20, right: 20, bottom: 40, left: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="sentiment">
                    <Label
                      value="Sentiment Type"
                      offset={0}
                      position="insideBottom"
                    />
                  </XAxis>
                  <YAxis>
                    <Label
                      value="Count"
                      angle={-90}
                      position="insideLeft"
                      style={{ textAnchor: "middle" }}
                    />
                  </YAxis>
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#8884d8"
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </div>
            </Col>
            <Col md={5}>
              <Table
                striped
                bordered
                hover
                responsive
                className="table-bordered"
                style={{ fontSize: "0.6rem" }}
              >
                <thead className="table-primary">
                  <tr>
                    <th>#</th>
                    <th>Suggestion</th>
                  </tr>
                </thead>
                <tbody>
                  {suggestions.map((suggestion, index) => (
                    <tr
                      key={index}
                      className={
                        index % 2 === 0 ? "table-light" : "table-secondary"
                      }
                    >
                      <td>{index + 1}</td>
                      <td>{suggestion}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
          </Row>

          <Table
            striped
            bordered
            hover
            responsive
            style={{ fontSize: "0.6rem" }}
          >
            <thead>
              <tr>
                <th>User Photo</th>
                <th>User Name</th>
                <th>Review</th>
                <th>Date</th>
                <th>Sentiment</th>
                <th>Stars</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {reviewData.map((review, index) => (
                <tr key={index}>
                  <td>
                    <Image
                      src={review.profile_photo || "https://via.placeholder.com/30"}
                      roundedCircle
                      width={30}
                      height={30}
                    />
                  </td>
                  <td>{review.user_name}</td>
                  <td>{review.review}</td>
                  <td>{new Date(review.date).toLocaleDateString()}</td>
                  <td>{review.sentiment}</td>
                  <td>{renderStars(review.score)}</td>
                  <td>{review.score}/5</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      <style jsx>{`
        .star {
          color: #ddd;
          font-size: 1.5em;
        }
        .filled {
          color: gold;
        }
        .empty {
          color: #ddd;
        }
      `}</style>
    </Container>
  );
};

export default ReviewAndRatingManagement;
